/* eslint-disable complexity */


import type { TabName } from "../TabData";
import ProfileHeader from "@/components/profile/ProfileHeader";
import { useCallback, useContext, useEffect, useState } from "react";
import { AuthContext } from "@/Realize";
import { type ProfileDataStats, type DailyHeadlineSubmissionPost } from "@/local-shared/returnTypes";
import social, { SUCCESS } from "@/api/social";
import LoadingIcon from "@/assets/img/icons/animated-r.svg?react";
import GlobalGUI from "@/util/GlobalGUI";
import {
    PrivacyState,
    type DailyHeadline,
    type DailyHeadlineSubmission,
    type DBAccount
} from "@/local-shared/model";
import UIListener from "../UIListener";
import ProfileEditor from "@/components/profile/ProfileEditor";
import { LoginButtons } from "@/components/PopUpLogin";
import HeadlineCreator from "@/components/profile/HeadlineCreator";
import { ProfileTab } from "./ProfileTypes";
import ProfileTabRender from "./ProfileTabs";
import ContextMenu from "@/components/shared/ContextMenu.";

import HTTPStatus from "@/realize-shared/HTTPStatus";

import ReportIcon from "@/assets/img/symbolic/report.svg?react";
import UniversalReportDialog from "@/components/UniversalReportDialog";
import ProfileHeadlinesView from "./ProfileHeadlinesView";
import NotFollowingYou from "@/components/profile/NotFollowingYou";
import { AnimatePresence, motion } from "framer-motion";
import NotAFollower from "@/components/profile/NotAFollower";

export type ProfileError =
    "NotAFollower" |
    "NotFollowing";

export default function Profile({
    setActiveTab,
    setHeadlineSubmissionID,
    profileID = null,
    onBackPress,
    refreshTrigger,
}: {
    setActiveTab: (tab: TabName) => void,
    setHeadlineSubmissionID: (data: {
        id: number;
        headline: string;
    } | null) => void;
    profileID?: string | null;
    onBackPress: () => void;
    refreshTrigger?: number;
})
{
    const [activeProfileTab, setActiveProfileTab] = useState<ProfileTab>(ProfileTab.HEADLINES);
    const userData = useContext(AuthContext);

    const [profileData, setProfileData] = useState<DBAccount | null>(null);

    const [profileSecurity, setProfileSecurity] = useState<PrivacyState>(PrivacyState.PUBLIC);

    const [profileStats, setProfileStats] = useState<ProfileDataStats>({
        followersCount: 0,
        headlinesCreated: 0,
        realizationsCount: 0,
    });

    const [isFollowing, setIsFollowing] = useState(false);
    const [isLoading, setLoading] = useState(true);
    const [realizations, setRealizations] = useState<DailyHeadlineSubmission[]>([]);
    const [editMode, setEditMode] = useState(false);
    const [headlineCreator, setHeadlineCreator] = useState(false);
    const [showProfileContextMenu, setShowProfileContextMenu] = useState(false);

    const [contentError, setContentError] = useState<ProfileError | null>(null);

    const [showReportDialog, setShowReportDialog] = useState(false);

    const [submissionView, setSubmissionView] = useState<
        {
            submissions: DailyHeadlineSubmissionPost[],
            headline: DailyHeadline
        }|boolean
    >(false);

    if (profileID !== null && profileID === userData?.id)
    {
        setActiveTab("profile" as TabName);
    }

    useEffect(
        () =>
        {
            const resetProfileData = (tab: unknown) =>
            {
                const data = tab as { profileId: number };


                if (!data.profileId)
                {
                    Logger.error("Reset Profile Data Error", "No profile ID provided");

                    return;
                }


                Logger.log("Resetting Profile Data");
                setProfileData(null);
                setIsFollowing(false);

                setProfileStats({
                    followersCount: 0,
                    headlinesCreated: 0,
                    realizationsCount: 0,
                });

                setRealizations([]);
                setActiveProfileTab(ProfileTab.HEADLINES);
                setLoading(true);

                refreshProfile(data.profileId.toString())
                    .catch((error) =>
                    {
                        Logger.error("Error resetting profile data", error);
                    });
            };

            UIListener.listen("resetProfileData", resetProfileData);

            return () =>
            {
                UIListener.unlisten("resetProfileData", resetProfileData);
            };
        },
        // eslint-disable-next-line react-compiler/react-compiler
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );

    const loadRealizations = useCallback(async () =>
    {
        Logger.log("Loading Realizations");

        if (!profileData)
        {
            return;
        }

        if (!userData)
        {
            return;
        }

        if ((profileSecurity === PrivacyState.FOLLOWERS || profileSecurity === PrivacyState.BOTH) && !isFollowing)
        {
            Logger.log("Profile is set to followers only, but user is not following the profile");
            setContentError("NotAFollower");

            return;
        }

        if (profileSecurity === PrivacyState.FOLLOWING || profileSecurity === PrivacyState.BOTH)
        {
            if (!await social.profileFollowingMe(parseInt(profileData.id.toString())))
            {
                Logger.log("Profile is set to following only, but the profile doesn't follow you.");
                setContentError("NotFollowing");

                return;
            }
        }


        social.getProfileRealizations(profileData.id)
            .then((data) =>
            {
                if (data === null)
                {
                    Logger.error("Error loading realizations", "No data returned");

                    return;
                }

                if (data.status as HTTPStatus !== SUCCESS)
                {
                    Logger.error("Error loading realizations", data.message);

                    return;
                }

                if (data.data.length === 0)
                {
                    Logger.error("Error loading realizations", "No realizations found for this profile");

                    return;
                }

                Logger.log("Realizations loaded successfully", data.data);

                setRealizations(data.data);
            })
            .catch((error) =>
            {
                Logger.error("Error loading realizations", error);
            });
    }, [isFollowing, profileData, profileSecurity, userData]);

    useEffect(() =>
    {
        if (contentError !== null)
        {
            Logger.log("Clear Profile Content");
            setRealizations([]);
            setLoading(false);
        }
    }, [contentError]);

    useEffect(() =>
    {
        if (profileData)
        {
            if (profileData.id.toString() === userData?.id.toString())
            {
                // If the profile is the user's own profile, set security to public
                setProfileSecurity(PrivacyState.PUBLIC);

                return;
            }

            if (profileData.settings.followersOnly && !profileData.settings.followingOnly)
            {
                setProfileSecurity(PrivacyState.FOLLOWERS);
            }
            else if (profileData.settings.followingOnly && !profileData.settings.followersOnly)
            {
                setProfileSecurity(PrivacyState.FOLLOWING);
            }
            else if (profileData.settings.followersOnly && profileData.settings.followingOnly)
            {
                setProfileSecurity(PrivacyState.BOTH);
            }
            else
            {
                setProfileSecurity(PrivacyState.PUBLIC);
            }
        }
    }, [profileData, userData?.id]);

    useEffect(() =>
    {
        setLoading(true);

        switch (activeProfileTab)
        {
            case ProfileTab.REALIZATIONS:
                loadRealizations().catch((error) =>
                {
                    Logger.error("Error loading realizations", error);
                })
                    .finally(() =>
                    {
                        setLoading(false);
                    });

                break;
            default:
                setLoading(false);

                break;
        }
    }, [activeProfileTab, loadRealizations, profileSecurity]);

    useEffect(() =>
    {
        GlobalGUI.setStyle({
            statusbarStyle: "light",
        });
    }, []);


    const refreshProfile = useCallback(async (profileID: string) =>
    {
        if (userData)
        {
            const data = await social.getProfile(parseInt(profileID, 10))
                .catch((error) =>
                {
                    Logger.error("Error loading profile", error);

                    return null;
                });

            if (data === null)
            {
                Logger.error("Error loading profile", "No data returned");

                return;
            }

            if (data.status as HTTPStatus !== HTTPStatus.OK)
            {
                Logger.error("Error loading profile", data.message);

                return;
            }

            localStorage.removeItem("profile_id");
            setProfileData(data.data.profile);
            setIsFollowing(data.data.is_following);
            setProfileStats(data.data.stats);
        }
    }, [userData]);

    useEffect(() =>
    {
        const triggerOpenProfileEditMode = () =>
        {
            Logger.log("Open profile edit mode modal triggered");
            setEditMode(!editMode);
        };

        UIListener.listen("openProfileEditMode", triggerOpenProfileEditMode);

        return () =>
        {
            UIListener.unlisten("openProfileEditMode", triggerOpenProfileEditMode);
        };
    }, [editMode]);

    // Listen to refresh trigger from parent component
    useEffect(() =>
    {
        if (refreshTrigger && refreshTrigger > 0 && profileID)
        {
            Logger.log("Refreshing Profile Data");

            refreshProfile(profileID).then(() =>
            {
                UIListener.trigger("RefreshProfileTab", {});
            })
                .catch((error) =>
                {
                    Logger.error("Error refreshing profile:", error);
                });
        }
    }, [refreshTrigger, profileID, refreshProfile]);

    useEffect(() =>
    {
        if (profileID !== null && profileID !== "")
        {
            // Load External Profile
            Logger.log("External Profile", profileID);

            setLoading(true);

            refreshProfile(profileID)
                .catch((error) =>
                {
                    Logger.error("Error loading external profile", error);
                })
                .finally(() =>
                {
                    setLoading(false);
                });
        }
        else
        {
            // Load Personal Profile
            if (userData)
            {
                Logger.log("Loading Personal Profile Data", userData);

                setLoading(true);

                refreshProfile(userData.id.toString())
                    .catch((error) =>
                    {
                        Logger.error("Error loading external profile", error);
                    })
                    .finally(() =>
                    {
                        setLoading(false);
                    });
            }
        }
    // eslint-disable-next-line react-compiler/react-compiler
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userData]);

    if (!userData && !isLoading)
    {
        return (
            <div
                className={Wind.style({
                    width: "w-full",
                    height: "h-full",
                    display: "flex",
                    flexDirection: "flex-col",
                    alignItems: "items-center",
                    justifyContent: "justify-center",
                    paddingX: "px-4",
                }).class}
            >
                <h1
                    className={Wind.style({
                        fontSize: "text-2xl",
                        fontWeight: "font-bold",
                        color: "text-[#6C3331]",
                        marginBottom: "mb-2",
                        textAlign: "text-center",
                        opacity: "opacity-90",
                        transition: "transition-opacity ease-in-out",
                        transitionDuration: "duration-300",
                    }).class}
                >
                    Login To Realize
                </h1>
                <p
                    className={Wind.style({
                        fontSize: "text-base",
                        color: "text-[#695946]",
                        textAlign: "text-center",
                        marginBottom: "mb-4",
                        opacity: "opacity-80",
                    }).class}
                >
                    Cannot view profile, please log in to continue.
                </p>
                <LoginButtons />
            </div>
        );
    }

    if (!userData)
    {
        // Show Login Button
        return (
            <div
                className={Wind.style({
                    flexGrow: "grow",
                    width: "w-full",
                    display: "flex",
                    flexDirection: "flex-col",
                    justifyContent: "justify-center",
                    alignItems: "items-center",
                }).class}
            >
                <div
                    className={Wind.style({
                        textAlign: "text-center",
                        display: "flex",
                        flexDirection: "flex-col",
                        width: "w-full",
                        paddingX: "px-6",
                    }).class}
                >
                    <div
                        className={Wind.style({
                            marginBottom: "mb-12",
                        }).class}
                    >
                        <h1
                            className={Wind.style({
                                color: "text-[#6C3331]",
                                fontSize: "text-4xl",
                                lineHeight: "leading-9",
                                fontFamily: "font-poppins",
                                fontWeight: "font-bold",
                                marginBottom: "mb-3",
                            }).class}
                        >
                            Login to Realize
                        </h1>
                        <p
                            className={Wind.style({
                                color: "text-[#6C3331]",
                                fontSize: "text-base",
                                lineHeight: "leading-[100%]",
                                fontFamily: "font-poppins",
                                fontWeight: "font-normal",
                            }).class}
                        >
                            Create your own Realizations
                        </p>
                    </div>
                    <LoginButtons />
                    <p
                        className={Wind.style({
                            color: "text-[#6C3331]",
                            fontSize: "text-sm",
                            lineHeight: "leading-[100%]",
                            fontFamily: "font-poppins",
                            fontWeight: "font-normal",
                            textAlign: "text-center",
                        }).class
                        }
                    >
                        By continuing, you agree to Realize&apos;s Terms of Use and Privacy Policy.
                    </p>
                </div>
            </div>
        );
    }


    if (submissionView === true || profileData === null)
    {
        return (
            <div>
                <LoadingIcon className="w-24 h-24 fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
            </div>
        );
    }

    if (typeof submissionView === "object" && submissionView.submissions.length > 0)
    {
        return (
            <ProfileHeadlinesView
                submissions={submissionView.submissions}
                headline={submissionView.headline}
                profile={profileData}
                resetSubmissions={() =>
                {
                    setSubmissionView(false);
                }}
            />
        );
    }

    return (
        <>
            <ProfileHeader
                profileData={profileData}
                activeProfileTab={activeProfileTab}
                isFollowing={isFollowing}
                profileStats={profileStats}
                setActiveTab={setActiveTab}
                setContextMenuVisible={setShowProfileContextMenu}
                refreshProfile={() =>
                {
                    Logger.log("Refreshing Profile Data");


                    refreshProfile(profileData.id.toString()).then(() =>
                    {
                        UIListener.trigger("RefreshProfileTab", {});
                    })
                        .catch((error) =>
                        {
                            Logger.error("Error refreshing profile", error);
                        });
                }}
                setActiveProfileTab={(tab) =>
                {
                    if ( tab === activeProfileTab)
                    {
                        return;
                    }

                    setActiveProfileTab(tab);
                }}
                showHeadlineCreator={() =>
                {
                    setHeadlineCreator(true);
                }}
                onBackPress={onBackPress}
            />
            <div className="flex-grow overflow-hidden relative">
                <div
                    className="
                    from-[#ffe5c5]
                    to-transparent
                    bg-gradient-to-b
                    absolute
                    top-0
                    left-0
                    right-0
                    h-4
                    z-[100]
                     "
                />
                <div className="overflow-y-auto h-full">
                    <AnimatePresence>
                        {isLoading && (
                            <motion.div
                                className={Wind.style({
                                    display: "flex",
                                    justifyContent: "justify-center",
                                    alignItems: "items-center",
                                    height: "h-full",
                                    flexDirection: "flex-col",
                                    gap: "gap-1",
                                }).class}
                                initial={{ opacity: 0, y: -20, scale: 0.95 }}
                                animate={{ opacity: 1, y: 0, scale: 1 }}
                                exit={{ opacity: 0, y: -20, scale: 0.95 }}
                                transition={{ duration: 0.3 }}
                            >
                                <LoadingIcon />
                                <span>Loading Profile</span>
                            </motion.div>
                        )}
                        {!isLoading && contentError === "NotAFollower" && (
                            <NotAFollower />
                        )}

                        {!isLoading && contentError === "NotFollowing" && (
                            <NotFollowingYou />
                        )}

                        {!isLoading && contentError === null
                    && (
                        <ProfileTabRender
                            security={profileSecurity}
                            realizations={realizations}
                            tab={activeProfileTab}
                            setContentError={setContentError}
                            isFollowing={isFollowing}
                            profileId={parseInt(profileData.id.toString(), 10)}
                            refreshProfile={() =>
                            {
                                refreshProfile(profileID?.toString() ?? "-1").catch((error) =>
                                {
                                    Logger.error("Error refreshing profile", error);
                                });
                            }}
                            showHeadlineCreator={() =>
                            {
                                setHeadlineCreator(true);
                            }}
                            showHeadlineCamera={(id: number, headline: string) =>
                            {
                                setHeadlineSubmissionID({id, headline});
                            }}
                            showSubmissions={(headline: DailyHeadline) =>
                            {
                                Logger.log("Loading submissions for headline ID:", headline.id);

                                setSubmissionView(true);

                                social.getDailySubmissions(headline.id).then((submissions) =>
                                {
                                    if (submissions === null)
                                    {
                                        Logger.error("Error loading submissions", "No data returned");

                                        return;
                                    }

                                    if (submissions.status as HTTPStatus !== SUCCESS)
                                    {
                                        Logger.error("Error loading submissions", submissions.message);

                                        return;
                                    }

                                    if (submissions.data.length === 0)
                                    {
                                        Logger.error(
                                            "Error loading submissions",
                                            "No submissions found for this headline"
                                        );

                                        return;
                                    }

                                    setSubmissionView({submissions: submissions.data, headline: headline});
                                })
                                    .catch((error) =>
                                    {
                                        Logger.error("Error loading submissions", error);
                                    })
                                    .finally(() =>
                                    {
                                        setLoading(false);
                                    });
                            }}
                        />
                    )}
                    </AnimatePresence>
                </div>
            </div>
            <div
                className={Wind.style({
                    position: "fixed",
                    inset: "inset-0",
                    display: "flex",
                    flexDirection: "flex-col",
                    alignItems: "items-center",
                    justifyContent: "justify-center",
                    backgroundColor: "bg-black/50",
                    zIndex: "z-[200]",
                    transition: "transition-opacity ease-in-out",
                    transitionDuration: "duration-300",
                    transitionDelay: "delay-100",
                    pointerEvents: editMode ? "pointer-events-auto" : "pointer-events-none",
                    backdropBlur: "backdrop-blur-sm",
                    opacity: editMode ? "opacity-100" : "opacity-0",
                }).class}
                onClick={(e) =>
                {
                    if (e.target === e.currentTarget)
                    {
                        setEditMode(false);
                    }
                }}
            >
                <ProfileEditor />
            </div>
            <div
                className={Wind.style({
                    position: "fixed",
                    inset: "inset-0",
                    display: "flex",
                    flexDirection: "flex-col",
                    alignItems: "items-center",
                    justifyContent: "justify-center",
                    zIndex: "z-[200]",
                    transition: "transition-opacity ease-in-out",
                    transitionDuration: "duration-300",
                    transitionDelay: "delay-100",
                    pointerEvents: headlineCreator ? "pointer-events-auto" : "pointer-events-none",
                    opacity: headlineCreator ? "opacity-100" : "opacity-0",
                }).class}
            >
                <HeadlineCreator
                    onClose={(refresh) =>
                    {
                        if (refresh && activeProfileTab === ProfileTab.HEADLINES)
                        {
                            Logger.log("Refreshing Profile Headlines");
                            UIListener.trigger("RefreshProfileTab", {});
                        }

                        setHeadlineCreator(false);
                    }}
                />
            </div>
            <ContextMenu
                visible={showProfileContextMenu}
                onClose={() =>
                {
                    setShowProfileContextMenu(false);
                }}
            >
                <ContextMenu.Item
                    icon={<ReportIcon className="w-6 h-6" />}
                    label="Report Profile"
                    onClick={() =>
                    {
                        setShowProfileContextMenu(false);
                        setShowReportDialog(true);
                    }}
                />
            </ContextMenu>
            <UniversalReportDialog
                isVisible={showReportDialog}
                reportedContent={{
                    user: `${profileData.id}`,
                    object_id: "profile",
                    timestamp: new Date().toISOString(),
                }}
                onClose={(reportSubmitted) =>
                {
                    if (reportSubmitted)
                    {
                        // TODO: Navigate Away from Profile

                        Logger.log("Report submitted successfully");
                        Logger.log("Navigating away from profile");
                    }

                    setShowReportDialog(false);
                }}
            />
        </>
    );
}
